import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import api from "../api/api";
import { invalidateMyProfileCache } from "../api/profileAPI";
export const AuthContext = createContext();

const AUTH_SNAPSHOT_KEY = "saar3_auth_snapshot";

// English:
// Read a lightweight session snapshot so protected routes do not feel blank
// while /me is still refreshing on slower mobile devices.
function readAuthSnapshot() {
  try {
    const raw = sessionStorage.getItem(AUTH_SNAPSHOT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeAuthSnapshot(user) {
  try {
    if (!user) {
      sessionStorage.removeItem(AUTH_SNAPSHOT_KEY);
      return;
    }

    sessionStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify(user));
  } catch {
    // English: Ignore storage failures silently.
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readAuthSnapshot());
  const [authReady, setAuthReady] = useState(false);
  const refreshPromiseRef = useRef(null);

  const syncUser = useCallback((nextUser) => {
    setUser(nextUser || null);
    writeAuthSnapshot(nextUser || null);
  }, []);

  const refreshMe = useCallback(async ({ force = false } = {}) => {
    if (!force && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const promise = (async () => {
      try {
        const res = await api.get("/api/users/me");
        const nextUser = res.data?.user || null;
        syncUser(nextUser);
        return nextUser;
      } catch {
        syncUser(null);
        return null;
      } finally {
        setAuthReady(true);
      }
    })();

    refreshPromiseRef.current = promise;

    try {
      return await promise;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [syncUser]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (identifier, password, rememberMe = false) => {
    try {
      invalidateMyProfileCache();
      await api.post("/api/users/login", {
        identifier,
        password,
        rememberMe,
      });

      const me = await refreshMe({ force: true });

      if (!me) {
        return {
          success: false,
          message: "Login failed. Session could not be established.",
        };
      }

      return { success: true, user: me };
    } catch (err) {
      syncUser(null);
      setAuthReady(true);

      return {
        success: false,
        message: err?.response?.data?.message || "Login failed",
        emailVerificationRequired: Boolean(
          err?.response?.data?.emailVerificationRequired
        ),
        email:
          err?.response?.data?.email ||
          String(identifier || "").trim().toLowerCase(),
      };
    }
  };

  const logout = async () => {
    try {
      await api.post("/api/users/logout");
    } catch {
      // English: Ignore logout errors.
    } finally {
  invalidateMyProfileCache(user);
  invalidateMyProfileCache();
  syncUser(null);
  setAuthReady(true);
}
  };

  return (
    <AuthContext.Provider
      value={{ user, authReady, login, logout, refreshMe }}
    >
      {children}
    </AuthContext.Provider>
  );
};

